import React, { useState } from 'react';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import GoogleLoginButton from './GoogleLoginButton';
import { useNavigate } from 'react-router-dom';
import TestConnection from './TestConnection';
import { useSearchParams } from 'react-router-dom';

const LoginForm = () => {
  const [searchParams] = useSearchParams();
  const verification = searchParams.get('verification');

  const { login, currentUser } = useAuth();
  const showVerifiedMessages = !currentUser && verification;
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const requestData = {
        email: formData.email,
        password: formData.password,
      };

      console.log('Sending login request...');
      console.log('Request data:', requestData);

      const response = await fetch(`${config.apiUrl}/login`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      console.log('Response received:', response);
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers));

      const responseText = await response.text();
      console.log('Response text:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed JSON data:', data);
      } catch (parseError) {
        console.log('Response is not JSON:', parseError);
        throw new Error('Invalid response format');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Extract user data from response
      const userData = {
        email: formData.email,
        ...data.user,
      };

      login(data.token, userData);

      toast({
        title: 'Success',
        description: 'Login successful!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showVerifiedMessages && (
        <Box bg={'green.100'} p={2} borderRadius='md'>
          <Text color={'green.800'}>You've successfully logged in. Please login to continue</Text>
        </Box>
      )}

      <Box width='100%'>
        <TestConnection />
        <Box as='form' onSubmit={handleSubmit} width='100%' mt={4}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name='email'
                type='email'
                value={formData.email}
                onChange={handleInputChange}
                placeholder='Enter your email'
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder='Enter your password'
                />
                <InputRightElement>
                  <IconButton
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowPassword(!showPassword)}
                    variant='ghost'
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <Button
              type='submit'
              colorScheme='blue'
              width='100%'
              isLoading={isLoading}
              loadingText='Logging in...'
              mb={4}
            >
              Login
            </Button>

            {/* <GoogleLoginButton />*/}
            <GoogleLoginButton />
            {/* <AppleLoginButton />*/}

            <Text fontSize='sm'>
              Don't have an account?{' '}
              <Button variant='link' colorScheme='blue' onClick={() => navigate('/register')}>
                Register here
              </Button>
            </Text>

            <Button
              variant='link'
              colorScheme='blue'
              size='sm'
              onClick={() => navigate('/forgot-password')}
            >
              Forgot Password?
            </Button>
          </VStack>
        </Box>
      </Box>
    </>
  );
};

export default LoginForm;
